import { init_storage_type, T_Storage_Value } from '@common/typings/cache';
import { msSessionStorage, msLocalStorage } from '@common/utils/cache';
import { getQueryString } from '@common/utils/common';
import getAppKey from './utils/getAppKey';
import getChannel from './utils/getChannel';
import dsBridge from '@common/assets/js/dsbridge';
import { getCookie } from '@common/stores/cookie';

// 各个子方向都需要用到的缓存队列
const BASE_STORAGE_LIST: init_storage_type = [
  {
    save_key: 'PUBLIC_CHANNEL',
    describe: '渠道',
    cb: () => getQueryString('channel'),
  },
  {
    save_key: 'PUBLIC_APPKEY',
    describe: '可平移代替channel，有细微差异，优先从query中读取，兜底从channel转换',
    cb: () => getQueryString('appKey') || getCookie('appKey') || getAppKey(),
  },
  {
    save_key: 'PUBLIC_APPNAME',
    describe: '当前产品名称',
    cb: () => 'SYH',
  },
  {
    save_key: 'PUBLIC_TEMPAPPLYNO',
    describe: '模板编号',
    cb: () => getQueryString('tempApplyNo'),
  },
  {
    save_key: 'PUBLIC_PRODUCTCODE',
    describe: '产品编号',
    cb: () => getQueryString('productCode'),
  },
  {
    save_key: 'PUBLIC_SOURCEWAY',
    describe: '来源方式：常用于二级渠道',
    cb: () => getQueryString('sourceway'),
  },
];

type T_BaseStorageParams = {
  extraList: init_storage_type; // 如果各子项目有不同基础缓存项，则可通过参数注入的方式传进来
};

export const initBaseStorage = (options?: T_BaseStorageParams) => {
  let INIT_STORAGE_LIST = BASE_STORAGE_LIST;
  if (options?.extraList.length) {
    INIT_STORAGE_LIST = BASE_STORAGE_LIST.concat(options.extraList);
  }

  // 开始初始化项目所需基础缓存
  for (const obj of INIT_STORAGE_LIST) {
    // 如果新获取的值和已缓存的值有差异，则进行替换
    const newValue = obj.cb();
    if (newValue) {
      // 使用localStorage存储，防止webview容器多开时通用信息丢失问题，sessionStorage使用较多，暂时保留，后续迁移至localStorage后删除
      msSessionStorage.set(obj.save_key, newValue);
      msLocalStorage.set(obj.save_key, newValue);
    }
  }
  // 进程id：根据进程id丰富曝光策略，实现更精细化的曝光策略
  // 获取app进程id
  if (dsBridge.hasNativeMethod('UtilsModule.getProcessId')) {
    dsBridge.call('UtilsModule.getProcessId', function (res: any) {
      try {
        res = typeof res === 'string' ? JSON.parse(res) : res;
        let processId = '';
        if (res?.code === '200' && res.data) {
          processId = res.data.processId || '';
        }
        msSessionStorage.set('PUBLIC_PROCESSID', processId);
      } catch (e) {
        // ignore
      }
    });
  }
};

// 定义为项目基础参数
export type BaseStoreType = {
  channel: string; // 当前无法去掉，在业务场景环节有依赖
  appKey: string; // 可根据channel推演出来，也可根据接口返回值赋值
  cooperatorId: string; // 渠道编码
  productCode: string; // 产品编码
  sourceway: string; // 来源方式：二级渠道
  appName: 'SYH'; // 应用名称：SYH、YYH
  system: 'SYH_H5'; // 所属系统标识，AYH：SYH_H5、YYH：LHH_H5
  processId: string; // app进程id
  userId: string; // 用户id
  uniqueId: string; // 用户uniqueId
  terminalAlias: string; // 终端别称
  refNbr: string; //贷中借据号
};
const getTerminalAlias = () => {
  const appKey = msLocalStorage.get('PUBLIC_APPKEY') ?? '';
  const channel = msLocalStorage.get('PUBLIC_CHANNEL') ?? getChannel() ?? '';
  const terminalAlias = appKey || channel || 'ayhdl';
  return terminalAlias.replace(/[-_]/g, '').toLowerCase();
};

/**
 * 使用基础信息
 * @returns
 */
export const useBaseStore = (): BaseStoreType => {
  return {
    channel: (msLocalStorage.get('PUBLIC_CHANNEL') ?? getChannel()) || 'ayhdl', // 使用localStorage级别缓存
    appKey: msLocalStorage.get('PUBLIC_APPKEY') ?? '', // 使用localStorage级别缓存
    // appKey: 'SYH_IOS',
    cooperatorId: msLocalStorage.get('PUBLIC_COOPERATORID') ?? '', // 使用localStorage级别缓存
    productCode: msSessionStorage.get('PUBLIC_PRODUCTCODE') ?? '', // 使用sessionStorage级别缓存
    sourceway: msSessionStorage.get('PUBLIC_SOURCEWAY') ?? '', // 使用sessionStorage级别缓存
    appName: 'SYH',
    system: 'SYH_H5',
    processId: msSessionStorage.get('PUBLIC_PROCESSID') ?? '', // 使用sessionStorage级别缓存
    userId: msSessionStorage.get('PUBLIC_USERID') ?? '', // 使用sessionStorage级别缓存
    uniqueId: msSessionStorage.get('PUBLIC_UNIQUEID') ?? '', // 使用sessionStorage级别缓存
    terminalAlias: getTerminalAlias(),
    refNbr: msLocalStorage.get('refNos') ?? '', // 贷中借据号
  };
};

export const updateLocalBaseStore = (key: string, value: T_Storage_Value): void => {
  msLocalStorage.set(key, value);
};
